import React, { useState, useEffect, useCallback } from "react";
import tw from "tailwind-styled-components";
import { FaTimes, FaEye, FaEyeSlash, FaUser, FaEnvelope, FaPhoneAlt, FaBuilding, FaLock, FaStore } from "react-icons/fa";
import { useDispatch } from "react-redux";
import axios from "axios";
// import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";
import Config from "../../Config";
import { Save_User } from "../../Redux/actions";
import { useNavigate } from "react-router-dom";

// Email validation
const emailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const passwordFormat =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
// password must be at least 8 characters, include uppercase, lowercase, number, and special character
// const phoneFormat = /^(\+91)?[6-9]\d{9}$/;

// props for signup modal
const SignupModal = ({ isOpen, onClose, onSwitchToLogin, onSignupSuccess }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: '',
    email: "",
    password: "",
    phone: "",
    companyName: "",
  });
  // const [registerAsSeller, setRegisterAsSeller] = useState(false);
  const [selectedRole, setSelectedRole] = useState(Config.userType.BUYER); // set the default user role as buyer when modal opens
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        firstName: "",
        lastName: '',
        email: "",
        password: "",
        phone: "",
        companyName: "",
      });
      // setRegisterAsSeller(false);
      setSelectedRole(Config.userType.BUYER);
      setAgreeToTerms(false);
      setErrors({});
      setShowPassword(false);
      setPasswordStrength(0);
    }
  }, [isOpen]);

  // password strength
  const calculatePasswordStrength = useCallback((password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[@$!%*?&]/.test(password)) strength += 1;
    return strength;
  }, []);

  useEffect(() => {
    // Update password strength when password changes
    if (formData.password) {
      setPasswordStrength(calculatePasswordStrength(formData.password));
    } else {
      setPasswordStrength(0);
    }
  }, [formData.password, calculatePasswordStrength]);

  if (!isOpen) return null;

  const validateField = (name, value) => {
    // Handle undefined/null values
    if (value === undefined || value === null) {
      value = "";
    }

    // Convert to string if it's not already
    value = String(value);

    switch (
      name // field specific validation
    ) {
      case "firstName":
        if (!value.trim()) return "Please enter your first name";
        if (value.trim().length < 3)
          return "First name must be at least 3 characters";
        if (value.trim().length > 20)
          return "First name must be less than 20 characters";
        if (!/^[a-zA-Z\s]+$/.test(value.trim()))
          return "Name can only contain letters and spaces";
        return "";

      case 'lastName':
        if (value.trim().length > 20) return 'Last name must be less than 20 characters';
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) return 'Name can only contain letters and spaces';
        return '';

      case "email":
        if (!value.trim()) return "Please enter your email address";
        if (!emailFormat.test(value))
          return "Please enter a valid email address";
        return "";

      case "password":
        if (!value) return "Please enter a password";
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!passwordFormat.test(value)) {
          return "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character";
        }
        return "";

      case "phone":
        if (!value.trim()) return "Please enter your phone number";
        if (!/^(\+91)?[6-9]\d{9}$/.test(value.trim()))
          return "Please enter a valid phone number";
        return "";

      // case 'email':
      //   // Only validate if email is not empty
      //   if (value.trim() && !emailFormat.test(value)) return 'Please enter a valid email address';
      //   return '';

      // case 'phone':
      //   // Only validate if an phone number is provided
      //   if (value.trim() && !phoneFormat.test(value.trim())) return 'Please enter a valid phone number';
      //   return '';

      case "companyName":
        // Optional field - will be validated separately based on role
        if (value && value.trim().length < 3)
          return "Company name must be at least 3 characters";
        return "";

      default:
        return "";
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear general error when user starts typing
    if (errors.general) {
      setErrors((prev) => ({
        ...prev,
        general: "",
      }));
    }

    // Clear email or phone error if the user is updating any of the fields
    // if (name === 'email' || name === 'phone') {
    //   if (errors.email === 'Please provide an email or a phone number') {
    //     setErrors(prev => ({
    //       ...prev,
    //       email: '',
    //       phone: ''
    //     }));
    //   }
    // }

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate required fields
    // const requiredFields = ['firstName', 'lastName', 'email', 'password', 'phone'];
    const requiredFields = ["firstName", "email", "phone", "password"];
    requiredFields.forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    // validate email format only if it's provided
    // if (formData.email && formData.email.trim()) {
    //     const emailError = validateField('email', formData.email);
    //     if (emailError) newErrors.email = emailError;
    // }

    // logic for email or phone
    // const emailValue = (formData.email || '').trim();
    // const phoneValue = (formData.phone || '').trim();

    // if (!emailValue && !phoneValue) {
    //   // Both are empty
    //   newErrors.email = 'Please provide an email or a phone number';
    //   newErrors.phone = 'Please provide an email or a phone number';
    // } else {
    //   // At least one is provided, validate whichever one(s) have values
    //   if (emailValue) {
    //     const emailError = validateField('email', emailValue);
    //     if (emailError) newErrors.email = emailError;
    //   }
    //   if (phoneValue) {
    //     const phoneError = validateField('phone', phoneValue);
    //     if (phoneError) newErrors.phone = phoneError;
    //   }
    // }

    // For sellers, company name is required
    // if (registerAsSeller) {
    if (selectedRole === Config.userType.SELLER) {
      if (!formData.companyName || !formData.companyName.trim()) {
        newErrors.companyName = "Company name is required for sellers";
      } else {
        const error = validateField("companyName", formData.companyName);
        if (error) newErrors.companyName = error;
      }
    } else {
      // For buyers and admins, validate company name only if provided
      if (
        formData.companyName &&
        typeof formData.companyName === "string" &&
        formData.companyName.trim()
      ) {
        const error = validateField("companyName", formData.companyName);
        if (error) newErrors.companyName = error;
      }
    }

    if (!agreeToTerms) {
      newErrors.terms =
        "Please agree to the Terms of Service and Privacy Policy";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateAccount = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // role checkbox
      // const userRole = registerAsSeller ? Config.userType.SELLER : Config.userType.BUYER;

      // Use selected role
      const userRole = selectedRole;

      // const emailValue = (formData.email || '').trim();
      // const phoneValue = (formData.phone || '').trim();

      // API call to register new user
      const response = await axios.post(`${Config.apiUrl}/auth/register`, {
        first_name: (formData.firstName || "").trim(),
        last_name: (formData.lastName || '').trim(),
        email: (formData.email || "").trim(),
        // email: emailValue || undefined, // Send undefined if empty
        password: formData.password || "",
        phone: (formData.phone || "").trim(),
        // phone: phoneValue || undefined, // Send undefined if empty
        role: userRole,
        company_name: (formData.companyName || "").trim() || undefined,
      });

      if (response.data && response.data.success) {
        // console.log('register data >> response >> data is >>', response.data)
        setErrors({}); // Clear errors only on successful registration

        // Save user data to Redux store
        const userData = {
          ...response.data.data.user,
          token: response.data.data.token,
        };

        dispatch(Save_User(userData));

        if (userData.role === Config.userType.SELLER) {
          navigate('/admin');
          // navigate to seller dashboard if the user is seller
        } else {
          navigate("/"); // navigate to home
        }

        onSignupSuccess && onSignupSuccess(userData);
        onClose();
      } else {
        setErrors({
          general:
            response.data?.message || "Registration failed. Please try again.",
        });
      }
    } catch (error) {
      console.error("Registration failed:", error);
      setErrors({
        general:
          error.response?.data?.message ||
          "Registration failed. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const getPasswordStrengthColor = (strength) => {
    if (strength < 2) return "bg-red-500";
    if (strength < 4) return "bg-yellow-600";
    return "bg-green-500";
  };

  const getPasswordStrengthText = (strength) => {
    if (strength < 2) return "Weak";
    if (strength < 4) return "Medium";
    return "Strong";
  };

  const isFormValid =
    (formData.firstName || "").trim() &&
    // (formData.lastName || "").trim() &&
    (formData.email || "").trim() &&
    // ((formData.email || '').trim() || (formData.phone || '').trim()) && // Check if either email or phone is present
    formData.password &&
    (formData.phone || "").trim() &&
    (selectedRole === Config.userType.SELLER
      ? (formData.companyName || "").trim()
      : true) &&
    // ((formData.email || '').trim() ? emailFormat.test(formData.email) : true) &&
    emailFormat.test(formData.email || "") &&
    passwordFormat.test(formData.password || "") &&
    agreeToTerms &&
    Object.keys(errors)
      .filter((key) => key !== "general")
      .every((key) => !errors[key]);

  return (
    <ModalOverlay>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FaTimes className="w-4 h-4" />
        </CloseButton>

        <ModalContent>
          <Title>Create Account</Title>

          {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}

          {/* Role Selection */}
          {/* <RoleSelectionGroup> */}
            {/* <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="registerAsSeller"
                checked={registerAsSeller}
                onChange={(e) => {
                  setRegisterAsSeller(e.target.checked);
                  if (errors.general) {
                    setErrors(prev => ({
                      ...prev,
                      general: ''
                    }));
                  }
                }}
              />
              <RoleCheckboxLabel htmlFor="registerAsSeller">
                Register as a Seller
              </RoleCheckboxLabel>
            </CheckboxWrapper> */}

            {/* <RoleTitle>Register as a:</RoleTitle>

            <RadioGroup>
              <RadioWrapper>
                <RadioInput
                  type="radio"
                  id="buyer"
                  name="userRole"
                  value={Config.userType.BUYER}
                  checked={selectedRole === Config.userType.BUYER}
                  onChange={(e) => {
                    setSelectedRole(e.target.value);
                    if (errors.general) {
                      setErrors((prev) => ({
                        ...prev,
                        general: "",
                      }));
                    }
                  }}
                />
                <RadioLabel htmlFor="buyer">Buyer</RadioLabel>
              </RadioWrapper>

              <RadioWrapper>
                <RadioInput
                  type="radio"
                  id="seller"
                  name="userRole"
                  value={Config.userType.SELLER}
                  checked={selectedRole === Config.userType.SELLER}
                  onChange={(e) => {
                    setSelectedRole(e.target.value);
                    if (errors.general) {
                      setErrors((prev) => ({
                        ...prev,
                        general: "",
                      }));
                    }
                  }}
                />
                <RadioLabel htmlFor="seller">Seller</RadioLabel>
              </RadioWrapper>

              <RadioWrapper>
                <RadioInput
                  type="radio"
                  id="admin"
                  name="userRole"
                  value={Config.userType.ADMIN}
                  checked={selectedRole === Config.userType.ADMIN}
                  onChange={(e) => {
                    setSelectedRole(e.target.value);
                    if (errors.general) {
                      setErrors((prev) => ({
                        ...prev,
                        general: "",
                      }));
                    }
                  }}
                />
                <RadioLabel htmlFor="admin">Admin</RadioLabel>
              </RadioWrapper>
            </RadioGroup>
          </RoleSelectionGroup> */}

          <Row>
            {/* First Name */}
            <InputGroup>
              <Label htmlFor="firstName">
                First Name <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  $hasError={!!errors.firstName}
                  disabled={isLoading}
                  className="pl-10"
                />
              </div>
              {errors.firstName && <ErrorText>{errors.firstName}</ErrorText>}
            </InputGroup>

            {/* Last Name */}
             <InputGroup>
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
                $hasError={!!errors.lastName}
                disabled={isLoading}
              />
              {errors.lastName && <ErrorText>{errors.lastName}</ErrorText>}
            </InputGroup> 
          </Row>
          <Row>
            {/* Email Input */}
            <InputGroup>
              <Label htmlFor="email">
                Email Address <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                  $hasError={!!errors.email}
                  disabled={isLoading}
                  className="pl-10"
                />
              </div>
              {errors.email && <ErrorText>{errors.email}</ErrorText>}
            </InputGroup>

            {/* Phone Input */}
            <InputGroup>
              <Label htmlFor="phone">
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <FaPhoneAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  $hasError={!!errors.phone}
                  disabled={isLoading}
                  className="pl-10"
                />
              </div>
              {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
            </InputGroup>
          </Row>

          <Row>
            {/* Company Name */}
            {selectedRole === Config.userType.SELLER && (
              <InputGroup>
                <Label htmlFor="companyName">
                  {/* Company Name {selectedRole === Config.userType.SELLER ? '*' : '(Optional)'} */}
                  Company Name <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <FaBuilding className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    id="companyName"
                    name="companyName"
                    placeholder="Company Name"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    $hasError={!!errors.companyName}
                    disabled={isLoading}
                    className="pl-10"
                  />
                </div>
                {errors.companyName && (
                  <ErrorText>{errors.companyName}</ErrorText>
                )}
              </InputGroup>
            )}

            {/* Password Input */}
            <InputGroup>
              <Label htmlFor="password">
                Password <span className="text-red-500">*</span>
              </Label>
              <PasswordContainer>
                <div className="relative w-full">
                  <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    $hasError={!!errors.password}
                    disabled={isLoading}
                    className="pl-10"
                  />
                  <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </PasswordToggle>
                </div>
              </PasswordContainer>

              {/* Password Strength Indicator */}
              {formData.password && (
                <PasswordStrength>
                  {/* <PasswordStrengthBar>
                  <PasswordStrengthFill 
                    $strength={passwordStrength} 
                    className={getPasswordStrengthColor(passwordStrength)}
                  />
                </PasswordStrengthBar> */}
                  <PasswordStrengthText $strength={passwordStrength}>
                    {getPasswordStrengthText(passwordStrength)}
                  </PasswordStrengthText>
                </PasswordStrength>
              )}

              {errors.password && <ErrorText>{errors.password}</ErrorText>}
            </InputGroup>
          </Row>

          {/* Terms and Conditions Checkbox */}
          <CheckboxContainer>
            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                id="agreeToTerms"
                checked={agreeToTerms}
                onChange={(e) => {
                  setAgreeToTerms(e.target.checked);
                  if (errors.general) {
                    setErrors((prev) => ({
                      ...prev,
                      general: "",
                    }));
                  }
                }}
              />
              <CheckboxLabel htmlFor="agreeToTerms">
                I agree to all the{" "}
                <TermsLink href="#" onClick={(e) => e.preventDefault()}>
                  Terms of Service
                </TermsLink>
                ,{" "}
                <TermsLink href="#" onClick={(e) => e.preventDefault()}>
                  Privacy Policy
                </TermsLink>{" "}
                and{" "}
                <TermsLink href="#" onClick={(e) => e.preventDefault()}>
                  Content Policies
                </TermsLink>
              </CheckboxLabel>
            </CheckboxWrapper>
            {errors.terms && <ErrorText>{errors.terms}</ErrorText>}
          </CheckboxContainer>

          {/* Create Account Button */}
          <CreateAccountButton
            onClick={handleCreateAccount}
            disabled={isLoading || !isFormValid}
            $isValid={isFormValid}
          >
            {isLoading ? "Creating Account..." : "Create account"}
          </CreateAccountButton>

          <Divider>
            <DividerLine />
            <DividerText>or</DividerText>
            <DividerLine />
          </Divider>

         
          {/* Switch to Login */}
          <SwitchText>
            Already have an account?{" "}
            <SwitchLink onClick={onSwitchToLogin}>Login</SwitchLink>
          </SwitchText>

          <SellerLink
            href={Config.sellerDomain}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaStore className="text-lg" />
            <span>Become a Supplier</span>
          </SellerLink>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

// Styled Components
const ModalOverlay = tw.div`
  fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 backdrop-blur-md 
`;

const ModalContainer = tw.div`
  bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto relative
`;

const CloseButton = tw.button`
  absolute top-8 right-8 text-gray-400 hover:text-gray-600 z-10 transition-colors
`;

const ModalContent = tw.div`
  p-8 py-10
`;

const Row = tw.div`
  flex flex-col space-y-1 md:flex-row md:space-x-4 mb-2
`;

const Title = tw.h2`
  text-2xl font-semibold text-kuality-blue mb-4
`;

const ErrorMessage = tw.div`
  bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm
`;

const InputGroup = tw.div`
  flex-1 mb-0 
`;

const PasswordContainer = tw.div`
  relative flex items-center
`;

const Input = tw.input`
  w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-kuality-blue text-gray-700 placeholder-gray-400 disabled:bg-gray-50 disabled:text-gray-500 transition-colors
  ${(props) => (props.$hasError ? "border-red-500 focus:border-red-600" : "")}
`;

const PasswordToggle = tw.button`
  absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors
`;

const PasswordStrength = tw.div`
  flex items-center justify-between mt-2
`;

// const PasswordStrengthBar = tw.div`
//   flex-1 h-2 bg-gray-200 rounded-full overflow-hidden mr-3
// `;

// const PasswordStrengthFill = tw.div`
//   h-full transition-all duration-300 rounded-full
//   ${props => `w-${(props.$strength / 5) * 100}%`}
// `;

const PasswordStrengthText = tw.span`
  text-xs font-medium
  ${(props) => {
    if (props.$strength < 2) return "text-red-500";
    if (props.$strength < 4) return "text-yellow-600";
    return "text-green-500";
  }}
`;

const ErrorText = tw.span`
  text-red-500 text-xs mt-1 block
`;

const CheckboxContainer = tw.div`
  mb-2
`;

const CheckboxWrapper = tw.div`
  flex items-start space-x-3
`;

const Checkbox = tw.input`
  mt-1 w-4 h-4 border-gray-300 rounded accent-[#053264]
`;

const CheckboxLabel = tw.label`
  text-sm text-gray-600 leading-relaxed
`;

const TermsLink = tw.a`
  text-kuality-blue hover:text-kuality-dark underline transition-colors
`;

const CreateAccountButton = tw.button`
  w-full py-3 rounded-lg font-medium transition-colors duration-200 mb-0.5 text-white
  ${(props) =>
    props.$isValid
      ? "bg-kuality-blue hover:bg-kuality-dark cursor-pointer"
      : "bg-gray-400 cursor-not-allowed"}
  ${(props) => (props.disabled ? "opacity-50 cursor-not-allowed" : "")}
`;

const Divider = tw.div`
  flex items-center my-0.5
`;

const DividerLine = tw.div`
  flex-1 h-px bg-gray-300
`;

const DividerText = tw.span`
  px-4 text-gray-500 text-sm
`;

const SwitchText = tw.p`
  text-center text-gray-600 text-sm mb-1
`;

const SwitchLink = tw.span`
  text-kuality-blue hover:text-kuality-dark cursor-pointer font-medium transition-colors
`;

const RoleSelectionGroup = tw.div`
  mb-3 p-4 border border-gray-200 rounded-lg bg-gray-50
`;

const RoleTitle = tw.h3`
  text-sm font-semibold text-gray-700 mb-3
`;

const RadioGroup = tw.div`
  flex space-x-4
`;

const RadioWrapper = tw.div`
  flex items-center space-x-3
`;

const RadioInput = tw.input`
  w-4 h-4 accent-[#053264]
`;

const RadioLabel = tw.label`
  text-sm text-gray-700 cursor-pointer font-medium
`;

const RoleCheckboxLabel = tw.label`
  text-sm text-gray-700 cursor-pointer font-medium
`;

const Label = tw.label`
  block text-gray-700 font-semibold text-sm mb-1
`;

const SellerLink = tw.a`
  w-full flex items-center justify-center space-x-2 border border-kuality-blue text-kuality-blue hover:shadow-md py-3 rounded-lg font-medium transition-colors duration-200 mt-2 cursor-pointer no-underline
`;

export default SignupModal;